import { inject, Injectable, signal } from '@angular/core';

import { catchError, concat, EMPTY, finalize, Observable, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

import { IBranchResponse, Entry, BranchStoreModality } from './../../core/interfaces';
import { ReservationService, WaitingListService } from '../../core/services';

@Injectable()
export class PortalDataService {
  readonly #snackBar = inject(MatSnackBar);
  readonly #reservationService = inject(ReservationService);
  readonly #waitingListService = inject(WaitingListService);

  branch = signal<IBranchResponse | null>(null);
  entry = signal<Entry | null>(null);
  branchModalities = signal<BranchStoreModality[] | null>(null);

  isLoadingExitQueue = signal(false);
  isLoadingBranchDetails = signal(false);
  isInsertingEntry = signal(false);
  isLoadingBranch = signal(false);
  isLoadingEntry = signal(false);

  initializeData(branchId: string | null, entryId: string | null): Observable<Entry | IBranchResponse> {
    this.isLoadingBranch.set(!!branchId);
    this.isLoadingEntry.set(!!entryId);

    return concat(
      branchId
        ? this.#reservationService.getBranchById(branchId).pipe(
            catchError(() => {
              throw 'Opss! Não foi possível carregar a filial. Tente novamente em alguns segundos.';
            })
          )
        : EMPTY,
      branchId && entryId
        ? this.#waitingListService.getEntryPositionData(entryId).pipe(
            catchError(() => {
              throw 'Opss! Não foi obter sua posição na fila. Tente novamente em alguns segundos.';
            })
          )
        : EMPTY
    ).pipe(
      tap((data) => {
        if ('entryId' in data) {
          this.entry.set(data);
        } else {
          this.branch.set(data);
        }
      }),
      finalize(() => {
        this.isLoadingBranch.set(false);
        this.isLoadingEntry.set(false);
      })
    );
  }

  bindBranchDetails(): void {
    if (this.branchModalities()) return;

    this.isLoadingBranchDetails.set(true);
    this.#reservationService
      .getBranchOpeningHours(this.branch()!.id)
      .pipe(finalize(() => this.isLoadingBranchDetails.set(false)))
      .subscribe({
        next: (res) => this.branchModalities.set(res),
        error: (err) => {
          console.error(err);
          this.#snackBar.open(
            'Opss! Não foi possível carregar os horários. Tente novamente em alguns segundos.',
            'Fechar',
            {
              verticalPosition: 'top',
              duration: 5000,
            }
          );
        },
      });
  }
}
